/**
 * GBGS: DIE NE3-LISTE AUF DEN BAUAUFGABEN UND DER ROBOTER (.347).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (11.10.2026)
 *   „lese alle informationen aus der webseite. alle aufträge dort die du
 *    findest sind meine aufträge, entweder schon abgeschlossen, neu oder in
 *    bearbeitung."
 *   „von der emfehlung bin ich bei variante B."
 *   „direkt buchen und per push informieren, dass es erfolgreich gebucht
 *    wurde. evtl. auch mit einem screenshot vom buchungsergebnis."
 *
 * Eine Zeile je Standort (KLS) mit seinen Bauaufgaben aus dem Telekom-Portal:
 * Status, Fälligkeit, Wartegrund mit Hashtag und Wiedervorlage. Der Bauauftrag
 * (Kundenauftrag) hängt als Zusatz dran, Bulk mit Arbeitsstand. Oben die
 * Warteschlange des Roboters mit Bild je Zeile.
 */
import { GrundOptionen, BulkOptionen, FreitextBausteine, freitextVorschlaege, WvHinweis, wvZielIso } from '../teile/wartegrundHaeufig.tsx';
import { useEffect, useState } from 'react';
import { Dateifenster } from '../teile/Dateifenster.tsx';
import { api, pushFrage, darf, datum, zeitpunkt, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Suchfeld } from '../teile/Schluesselsuche.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

export const TYP: Record<string, string> = {
  PIPE_TO_BUILDING: 'Hausstich', GFAP_INSTALLATION: 'Gf-AP', VZK_INJECTION: 'Vzk', INITIAL_CABINET_SPLICING: 'NVt',
  GFTA_INSTALLATION_GFAP: 'Gf-TA', GFTA_INSTALLATION: 'Gf-TA', TRENCH: 'Längstrasse', SITE_SURVEY_FTTH: 'Auskundung',
};
export const STATUS: Record<string, { wort: string; farbe: string }> = {
  INITIAL: { wort: 'initial', farbe: 'z-neu' }, ACTIVE: { wort: 'aktiv', farbe: 'z-laufend' },
  IN_PROGRESS: { wort: 'in Arbeit', farbe: 'z-laufend' }, HELD: { wort: 'wartet', farbe: 'z-unterbrochen' },
  COMPLETED: { wort: 'fertig', farbe: 'z-erledigt' }, PENDING_CANCELLATION: { wort: 'Storno ausstehend', farbe: 'z-unterbrochen' },
  /* ABGEBROCHEN IN ROT (.473). Marvin, 23.10.2026: „wenn ein bereich abgebrochen ist, dann am besten rot oder orange anzeigen“ */
  CANCELLED: { wort: 'abgebrochen', farbe: 'z-ueberfaellig' },
};
const OFFEN = ['INITIAL', 'ACTIVE', 'IN_PROGRESS', 'HELD'];
export const istOffen = (a: any) => OFFEN.includes(String(a.status));
export const AUFTRAG: Record<string, string> = {
  PLANNED: 'geplant', RELEASED_FOR_INSTALLATION: 'freigegeben', ON_HOLD: 'zurückgestellt', INVENTORY_INSTALLED: 'installiert',
  CANCELED: 'abgebrochen', INITIAL: 'initial', IN_INSTALLATION: 'in Installation',
};
export const BULKSTAND: Record<string, string> = {
  SET_APPOINTMENT: 'Termin vereinbaren', NE3_EXECUTION: 'NE3 in Ausführung', NE3_AND_NE4_EXECUTION: 'NE3 und NE4 in Ausführung',
  EXPLORATION_PENDING: 'Auskundung offen', WAITING_STATE: 'wartet', WAITING_FOR_PREWORK: 'wartet auf Vorarbeiten',
  SALES_ADVICE_NEEDED: 'Vertriebsberatung', NVT_BLOCKING: 'NVt blockiert', ORDER_COMPLETED: 'fertig',
};
export const ART: Record<string, string> = {
  gfap_buchen: 'Gf-AP-Termin buchen', gfap_aendern: 'Gf-AP-Termin verschieben', gfap_stornieren: 'Gf-AP-Termin stornieren',
  bulk_termin: 'Bulk-Termin setzen', wartegrund: 'Wartegrund anlegen', hausstich_abschliessen: 'Hausstich abschließen', hausstich_oeffnen: 'Hausstich erneut öffnen', wartegrund_loesen: 'Wartegrund lösen (nicht am Gf-AP)', auskundung_holen: 'Auskundung holen (NE4)', ne4_bulk_termin: 'NE4-Termine ins Bulk-Portal',
};
export const ZUSTAND: Record<string, { wort: string; farbe: string }> = {
  hand: { wort: 'von Hand', farbe: 'z-neu' }, wartet: { wort: 'wartet', farbe: 'z-laufend' }, laeuft: { wort: 'läuft', farbe: 'z-laufend' },
  erledigt: { wort: 'erledigt', farbe: 'z-erledigt' }, fehler: { wort: 'nicht geschafft', farbe: 'z-ueberfaellig' },
  probelauf: { wort: 'Probelauf', farbe: 'z-unterbrochen' },
};
type Filter = 'offen' | 'ueberfaellig' | 'wartegrund' | 'ohne_wartegrund' | 'wiedervorlage' | 'buchbar' | 'ibt' | 'bulk' | 'alle';

const heute = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());
const anschrift = (o: any) => o ? `${o.strasse ?? ''} ${o.hausnummer ?? ''}${o.hausnummer_zusatz ?? ''}`.trim() + (o.ort ? `, ${o.plz ?? ''} ${o.ort}`.replace(/ ,/, ',') : '') : '';
/* DER STANDORT IN DER WARTESCHLANGE, ZWEIZEILIG (.363). Marvin, 14.10.2026:
   „die standorte linksbündig, wenn es nicht in eine zeile passt dann mit PLZ
   und ort in die zweite zeile". Straße und Hausnummer oben, PLZ und Ort
   darunter; der Knopf bricht nicht mehr mitten im Ortsnamen um. */
const Standort = ({ o }: { o: any }) => {
  const strasse = `${o?.strasse ?? ''} ${o?.hausnummer ?? ''}${o?.hausnummer_zusatz ?? ''}`.trim();
  const ort = `${o?.plz ?? ''} ${o?.ort ?? ''}`.trim();
  if (!strasse && !ort) return <>ohne Anschrift</>;
  return <><b>{strasse || ort}</b>{strasse && ort ? <span className="standort-ort">{ort}</span> : null}</>;
};

export default function Gbgs({ ich, oeffne }: { ich: Anmelder; oeffne: (auftragId: string, reiter?: string) => void }) {
  const [stand, setStand] = useState<any>(null);
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [schlange, setSchlange] = useState<any[]>([]);
  const [filter, setFilter] = useState<Filter>('offen');
  /* SAMMEL-WARTEGRUND (.437): Auswahl im Filter „ohne Wartegrund". */
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [sammel, setSammel] = useState(false);
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [wg, setWg] = useState<any | null>(null);
  const [bild, setBild] = useState<string | null>(null);
  const darfEintragen = darf(ich, 'gbgs.eintragen');

  const laden = async () => {
    try {
      const [s, z, q] = await Promise.all([api.gbgsStand(), api.gbgsStandorte(filter, suche.trim() || null), api.gbgsWarteschlange()]);
      setStand(s); setZeilen(z); setSchlange(q);
    } catch (e) { setFehler(ausFehler(e)); }
  };
  useFrisch(laden);
  // Filter und Suche laden neu (Suche mit kleiner Pause, damit nicht jedes Zeichen fragt).
  useEffect(() => { const t = setTimeout(() => { void laden(); }, suche ? 350 : 0); return () => clearTimeout(t); }, [filter, suche]); // eslint-disable-line react-hooks/exhaustive-deps

  /* DER ORANGE KASTEN GILT, SOLANGE ETWAS LÄUFT (.462). Marvin, 22.10.2026:
     „Hier ist dasselbe mit dem Laden und läuft. Bitte anpassen, dass der
     Block auch verschwindet." Nach „Jetzt lesen" bis die Lesung beendet ist,
     nach einem Roboter-Knopf bis keine Zeile mehr wartet oder läuft; bis dahin
     wird alle 5 Sekunden nachgesehen. */
  const [beobachten, setBeobachten] = useState<null | { art: 'lesen' | 'roboter'; seit: number }>(null);
  const tun = async (f: () => Promise<any>, art: 'lesen' | 'roboter' = 'roboter') => {
    setFehler(null);
    const seit = Date.now();
    try { const r = await f(); if (r?.klartext) setHinweis(r.klartext); await laden(); if (r?.klartext) setBeobachten({ art, seit }); }
    catch (e) { setFehler(ausFehler(e)); }
  };
  useEffect(() => {
    if (!beobachten) return;
    const t = setInterval(() => { void laden(); }, 5000);
    return () => clearInterval(t);
  }, [beobachten]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!beobachten) return;
    const lz = stand?.lesung;
    const fertig = beobachten.art === 'lesen'
      ? !!lz?.beendet_am && new Date(lz.beendet_am).getTime() >= beobachten.seit - 5000
      : !schlange.some((q: any) => q.zustand === 'wartet' || q.zustand === 'laeuft');
    if (fertig) { setHinweis(null); setBeobachten(null); }
  }, [stand, schlange, beobachten]);

  const z = stand?.zahlen;
  const sch = stand?.schalter;
  const l = stand?.lesung;
  const kachel = (f: Filter, titel: string, zahl: number | undefined, text: string, an = false) => (
    <button type="button" className={`schrittkachel ${filter === f ? 'an' : ''} ${an ? 'warn' : ''}`} onClick={() => setFilter(f)} aria-pressed={filter === f}>
      <span className="titel">{titel}</span>
      <span className="zahl">{zahl ?? '…'}</span>
      <span className="klein leise">{text}</span>
    </button>
  );

  return (
    <>
      <Kopf titel="GBGS"
            weg="Die Bauaufgaben aus dem Telekom-Portal je Standort, mit Wartegrund und Fälligkeit, und der Roboter"
            rechts={darfEintragen && (
              <span className="reihe" style={{ gap: 8 }}>
                {/* DIE TEXTVORLAGEN STEHEN GANZ UNTEN (.423). Marvin, 20.10.2026:
                    „Wo sind die Textvorlagen GBGS? Denn die finde ich nicht."
                    Unter hundert Standorten sah sie niemand; der Knopf springt hin. */}
                <button className="knopf klein" onClick={() => document.getElementById('gbgs-textvorlagen')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Textvorlagen</button>
                <button className="knopf klein" onClick={() => tun(() => api.gbgsLesen(), 'lesen')}>Jetzt lesen</button>
              </span>
            )} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}

        {/* WANN ZULETZT GELESEN WURDE, STEHT GROSS DA (.462). Marvin: „auch
            wann gelaufen ist. Das ist nämlich ein bisschen klein." */}
        <div className="gbgs-stand">
          {l ? <><b>{!l.beendet_am ? 'Liest gerade, begonnen ' : 'Zuletzt gelesen '}{zeitpunkt(l.beendet_am ?? l.begonnen_am)} Uhr</b>
            <span className="klein leise">, {l.bauaufgaben ?? 0} Bauaufgaben, {l.bauauftraege ?? 0} Bauaufträge, {l.bulk ?? 0} Bulk, {l.gebaeudeteile ?? 0} Gebäudeteile</span></> : <b>Noch nie gelesen.</b>}
        </div>
        <div className="klein leise" style={{ marginBottom: 8 }}>
          {l?.fehler && <span className="rot"> Fehler: {l.fehler}</span>}
          {sch && <>, Lesen {sch.lesen ? 'an' : 'aus'}, Roboter {sch.roboter ? 'an' : 'aus'}{sch.roboter ? (sch.scharf ? ', löst aus' : ', Probelauf (GBGS_SCHARF = nein)') : ''}, Gf-AP ohne Endzeit {sch.dauer_min} Minuten</>}
        </div>

        <div className="schrittkacheln gbgs-kacheln">
          {kachel('offen', 'Standorte offen', z?.standorte_offen, `${z?.bauaufgaben_offen ?? '…'} offene Bauaufgaben`)}
          {kachel('ueberfaellig', 'Überfällig', z?.ueberfaellig, 'Bauaufgaben über der Fälligkeit', !!z?.ueberfaellig)}
          {kachel('wartegrund', 'Mit Wartegrund', z?.mit_wartegrund, 'Bauaufgaben mit Wartegrund')}
          {kachel('ohne_wartegrund', 'Ohne Wartegrund', z?.ohne_wartegrund, 'Gf-AP offen, kein Termin, kein Wartegrund', !!z?.ohne_wartegrund)}
          {kachel('wiedervorlage', 'Wiedervorlage', z?.wiedervorlage, 'Wiedervorlage heute oder vorbei', !!z?.wiedervorlage)}
          {kachel('buchbar', 'Gf-AP buchbar', z?.buchbar, 'Standorte, an denen das Portal einen Termin anbietet')}
          {kachel('ibt', 'IBT offen', z?.ibt_offen, 'offene Standorte, die nicht Bulk sind')}
          {kachel('bulk', 'Bulk offen', z?.bulk_offen, 'Gebäudeaufträge im Bulk-Portal')}
        </div>

        <Karte titel={`Warteschlange${z ? ` (${z.warteschlange} läuft oder wartet, ${z.handarbeit} von Hand)` : ''}`}
               rechts={<span className="reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                 <span className="klein leise">Was der Roboter im Portal tut oder getan hat, je Zeile mit Bild</span>
                 {darfEintragen && ((z?.handarbeit ?? 0) > 0 || (z?.probelaeufe ?? 0) > 0) && (
                   <button className="knopf klein" onClick={async () => {
                     if (await fragen(`${z!.handarbeit} Zeilen von der Handliste, gescheiterte und (wenn GBGS_SCHARF auf ja steht) Probeläufe zurück an den Roboter? Überholte werden dabei erledigt.`)) void tun(() => api.gbgsHandInWarteschlange());
                   }}>Alle noch einmal ({z!.handarbeit}{(z?.probelaeufe ?? 0) > 0 ? ` + ${z!.probelaeufe} Probeläufe` : ''})</button>
                 )}
               </span>}>
          <SchlangeTabelle ich={ich} schlange={schlange} tun={tun} oeffne={oeffne} setBild={setBild} />
        </Karte>

        <Karte titel="Standorte"
               rechts={<span className="klein leise">eine Zeile je KLS, die früheste Fälligkeit zuerst</span>}>
          <div className="reihe anstehendfilter" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Suchfeld wert={suche} setze={setSuche} mehr="Bestellnummer" breit={320} />
            {([['offen', 'offen'], ['ueberfaellig', 'überfällig'], ['wartegrund', 'mit Wartegrund'], ['ohne_wartegrund', 'ohne Wartegrund'], ['wiedervorlage', 'Wiedervorlage fällig'],
               ['buchbar', 'Gf-AP buchbar'], ['ibt', 'IBT'], ['bulk', 'Bulk'], ['alle', 'alle']] as const).map(([f, t]) => (
              <button key={f} type="button" className={`knopf klein ${filter === f ? 'haupt' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>{t}</button>
            ))}
          </div>
          {/* SAMMEL-WARTEGRUND (.437). ORIGINALTEXT MARVIN (20.10.2026): „die
              neuen Aufträge müssen erstmal mit einem Wartegrund dann von uns
              versehen werden, wenn wir den Kunden nicht erreichen. Der Grund
              ist ja dann überall der gleiche ... eine Möglichkeit zur Selektion
              und dann zur Auswahl der Wartegründe aus den Vorlagen." */}
          {filter === 'ohne_wartegrund' && darfEintragen && !!zeilen?.length && (
            <div className="reihe" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0' }}>
              <label className="klein"><input type="checkbox" checked={gewaehlt.size > 0 && zeilen.every((s) => gewaehlt.has(s.kls_id))}
                onChange={(e) => setGewaehlt(e.target.checked ? new Set(zeilen.map((s) => s.kls_id)) : new Set())} /> alle {zeilen.length} sichtbaren wählen</label>
              <button type="button" className="knopf klein haupt" disabled={!gewaehlt.size} onClick={() => setSammel(true)}>Wartegrund für Auswahl ({gewaehlt.size})</button>
            </div>
          )}
          {!zeilen ? <p className="klein leise">Wird geladen.</p>
            : !zeilen.length ? <Leer text={l ? 'Kein Standort passt zu Suche und Filter.' : 'Noch nichts gelesen. „Jetzt lesen" holt den Stand aus dem Portal.'} />
            : (
              <div className="anstehendrahmen">
                <table className="liste anstehend gbgs-liste">
                  <thead><tr>{filter === 'ohne_wartegrund' && darfEintragen && <th></th>}<th>Standort</th><th>Bauaufgaben</th><th>Bauauftrag</th><th></th></tr></thead>
                  <tbody>
                    {zeilen.map((s) => {
                      const ort = s.ort ?? s.bulk ?? (s.bauauftraege?.[0] ?? null);
                      const aufgaben: any[] = s.bauaufgaben ?? [];
                      const teile: any[] = s.gebaeudeteile ?? [];
                      return (
                        <tr key={s.kls_id} style={{ cursor: 'default' }}>
                          {filter === 'ohne_wartegrund' && darfEintragen && (
                            <td><input type="checkbox" checked={gewaehlt.has(s.kls_id)}
                              onChange={(e) => setGewaehlt((g) => { const n = new Set(g); if (e.target.checked) n.add(s.kls_id); else n.delete(s.kls_id); return n; })} /></td>
                          )}
                          <td>
                            {s.auftrag?.id ? <button className="alslink" onClick={() => oeffne(s.auftrag.id)}><b>{anschrift(ort) || 'ohne Anschrift'}</b></button> : <b>{anschrift(ort) || 'ohne Anschrift'}</b>}
                            <div className="klein leise">KLS {s.kls_id}{s.ort?.nvt ? `, ${s.ort.nvt}` : ''}{s.ort?.gebiet ? `, ${s.ort.gebiet}` : ''}</div>
                            <div className="klein leise">
                              {s.ort?.modus === 'BFO' || s.bulk ? <span className="merker">Bulk</span> : null}
                              {s.ort?.we != null ? ` ${s.ort.we} WE${s.ort.ge ? `, ${s.ort.ge} GE` : ''}` : ''}
                              {s.ort?.ne4 && s.ort.ne4 !== 'APPROVED' ? `, NE4 ${s.ort.ne4 === 'POSTPONED' ? 'verschoben' : s.ort.ne4.toLowerCase()}` : ''}
                              {!s.auftrag?.id ? ', nicht in der Plattform' : ''}
                            </div>
                          </td>
                          <td>
                            <div className="gbgs-aufgaben">
                              {aufgaben.map((a) => {
                                const st = STATUS[a.status] ?? { wort: a.status, farbe: '' };
                                const ueber = istOffen(a) && a.faellig_am && String(a.faellig_am).slice(0, 10) < heute();
                                return (
                                  <div key={a.bid} className="gbgs-aufgabe">
                                    <span className={`marke-zustand ${st.farbe}`}>{TYP[a.typ] ?? a.typ}: {st.wort}</span>
                                    {istOffen(a) && a.faellig_am && <span className={`klein ${ueber ? 'rot' : 'leise'}`}> fällig {datum(a.faellig_am)}</span>}
                                    {a.grund && <div className="klein">Wartegrund: {a.hashtag ? `#${a.hashtag}# ` : ''}{a.kategorie ?? ''}{a.kategorie ? ': ' : ''}{a.grund}{a.wiedervorlage ? <span className={String(a.wiedervorlage).slice(0, 10) <= heute() ? ' rot' : ' leise'}>, WV {datum(a.wiedervorlage)}</span> : null}{a.gesetzt_am ? <span className="leise">, gesetzt {zeitpunkt(a.gesetzt_am)}</span> : null}</div>}
                                    {a.termin_von && <div className="klein leise">Termin {zeitpunkt(a.termin_von)}</div>}
                                  </div>
                                );
                              })}
                              {s.bulk && (
                                <div className="gbgs-aufgabe">
                                  <span className="marke-zustand z-laufend">Bulk: {BULKSTAND[s.bulk.arbeitsstand] ?? s.bulk.arbeitsstand}</span>
                                  {s.bulk.gfap_waehlbar && <span className="klein">, Gf-AP-Termin wählbar</span>}
                                  {s.bulk.gfap_termin && <span className="klein leise">, Gf-AP {zeitpunkt(s.bulk.gfap_termin)}</span>}
                                  {s.bulk.wartegrund_grund && <div className="klein">Wartegrund: {s.bulk.wartegrund_grund}{s.bulk.wartegrund_wiedervorlage ? `, WV ${datum(s.bulk.wartegrund_wiedervorlage)}` : ''}</div>}
                                </div>
                              )}
                              {teile.some((t) => t.buchbar) && <div className="klein gruen">Gf-AP-Termin im Portal buchbar{teile.some((t) => t.vorarbeiten === false) ? ' (Vorarbeiten noch offen)' : ''}</div>}
                              {teile.find((t) => t.termin_status === 'TIMESLOT_BOOKED') && <div className="klein">Gf-AP gebucht: {zeitpunkt(teile.find((t) => t.termin_status === 'TIMESLOT_BOOKED').von)}</div>}
                            </div>
                          </td>
                          <td>
                            {(s.bauauftraege ?? []).slice(0, 3).map((o: any) => (
                              <div key={o.bid} className="klein" style={{ marginBottom: 4 }}>
                                <b>{o.bestellnummer ?? o.bid}</b>, {AUFTRAG[o.status] ?? o.status}{o.typ === 'FIBER_ACCESS_APARTMENT' ? ', Wohnung' : o.typ === 'FIBER_ACCESS_BUILDING' ? ', Haus' : ''}
                                {o.gfap_von && <div className="leise">Gf-AP {zeitpunkt(o.gfap_von)}</div>}
                                {o.installation_von && <div className="leise">GfTA {zeitpunkt(o.installation_von)}</div>}
                                {o.ne4_firma && <div className="leise">NE4: {o.ne4_firma}</div>}
                              </div>
                            ))}
                            {!(s.bauauftraege ?? []).length && <span className="klein leise">kein Kundenauftrag</span>}
                          </td>
                          <td className="knopfleiste">
                            {darfEintragen && (aufgaben.some(istOffen) || s.bulk) && (
                              <button className="knopf klein" onClick={() => setWg(s)}>Wartegrund</button>
                            )}
                            {(s.warteschlange ?? []).length > 0 && <span className="klein leise">{(s.warteschlange ?? []).length} in der Warteschlange</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </Karte>

        {darfEintragen && <Textvorlagen />}
      </div>

      {wg && <WartegrundFenster standort={wg} ich={ich} zu={() => setWg(null)} fertig={(t) => { setWg(null); setHinweis(t); void laden().then(() => setBeobachten({ art: 'roboter', seit: Date.now() })); }} />}
      {sammel && <SammelWartegrundFenster standorte={(zeilen ?? []).filter((s) => gewaehlt.has(s.kls_id))} ich={ich} zu={() => setSammel(false)}
        fertig={(t) => { setSammel(false); setGewaehlt(new Set()); setHinweis(t); void laden().then(() => setBeobachten({ art: 'roboter', seit: Date.now() })); }} />}
      {bild && (
        /* DAS BILD IM DATEIFENSTER (.447). ORIGINALTEXT MARVIN (21.10.2026,
           Telefon): „Oben wegräumen. Hier das Bild als Pop-up auf dem Handy
           lässt sich nicht zoomen mit den Fingern." Die Seite sperrt den Zoom
           des Browsers (keinzoom.ts); gezoomt wird in der Zoomfläche, die
           auch Fotos und PDFs haben: zwei Finger, Doppeltipp, Rad, Knöpfe,
           ohne Rahmen und Kopfzeile. */
        <Dateifenster datei={{ id: bild, dateiname: 'Bild aus dem GBGS.png', mime: 'image/png', adresse: `/api/gbgs/bild/${bild}` }}
                      zu={() => setBild(null)} />
      )}
    </>
  );
}

/* DER SAMMEL-WARTEGRUND (.437): ein Grund, eine Wiedervorlage, eine Vorlage
   für viele Standorte; der Kommentar wird je Standort aus der Vorlage gefüllt
   (Adresse, KLS), genau wie einzeln. IBT-Standorte bekommen den Hashtag, Bulk-
   Standorte den Bulk-Grund. */
/* .599: DIE TABELLE DER WARTESCHLANGE, für NE3 (Seite GBGS) und NE4 (Seite NE4, GBGS) dieselbe.
   Marvin, 02.11.2026: „Hier bei GBGS wünsche ich mir den gleichen Aufbau wie bei NE3 ... da, wo es hängen
   geblieben ist, ich kann es manuell anstoßen, die Erledigten, ja, identischer Aufbau." */
export function SchlangeTabelle({ ich, schlange, tun, oeffne, setBild, leer = 'Nichts in der Warteschlange.' }: {
  ich: Anmelder; schlange: any[]; tun: (f: () => Promise<any>) => unknown; oeffne?: (auftragId: string) => void; setBild: (id: string) => void; leer?: string;
}) {
  const darfEintragen = darf(ich, 'gbgs.eintragen');
  if (!schlange.length) return <Leer text={leer} />;
  return (

            <div className="tabellenrahmen">
              <table className="liste">
                <thead><tr><th>Wann</th><th>Was</th><th>Standort</th><th>Termin</th><th>Stand</th><th></th></tr></thead>
                <tbody>
                  {schlange.map((q) => {
                    const n = q.nutzlast ?? {}; const zs = ZUSTAND[q.zustand] ?? { wort: q.zustand, farbe: '' };
                    return (
                      <tr key={q.id} style={{ cursor: 'default' }}>
                        <td className="klein">{zeitpunkt(q.updated_at ?? q.created_at)}</td>
                        <td>{q.art === 'bulk_termin' && n.was === 'geloescht' ? 'Bulk-Absage: Wartegrund setzen'
                              : q.art === 'bulk_termin' && n.was === 'verschoben' ? 'Bulk-Termin verschieben'
                              : (ART[q.art] ?? q.art)}{n.bulk ? <span className="merker">Bulk</span> : null}{n.grund_text ? <div className="klein leise">{n.hashtag ? `#${n.hashtag}# ` : ''}{n.grund_text}</div> : null}</td>
                        <td>
                          {q.auftrag_id && oeffne ? <button className="alslink standort" onClick={() => oeffne(q.auftrag_id)}><Standort o={n} /></button> : n.ne4_auftrag_id ? <a className="alslink standort" href={`#/ne4/ne4_auftraege/${n.ne4_auftrag_id}`}><Standort o={n} /></a> : <span className="standort"><Standort o={n} /></span>}
                          <div className="klein leise">{q.kls_id ? `KLS ${q.kls_id}` : ''}</div>
                        </td>
                        {/* TERMIN, FIRMA, MONTEUR (.356). Marvin, 13.10.2026: „wenn hier termine
                            gesetzt werden müssen, warum steht dann keine firma hinten drin? oder
                            müssen die storniert werden? Dann stimmt die info nicht mehr."
                            Eine Absage zeigt den abgesagten Termin als „bisher", die Firma steht
                            bei jeder Zeile, der Monteur nur dort, wo im Portal gebucht wird. */}
                        <td className="klein">
                          {n.datum
                            ? <>{datum(n.datum)} {n.von ?? ''}{n.bis ? `–${n.bis}` : ''}
                                {n.alt_datum && <div className="leise">bisher {datum(n.alt_datum)} {n.alt_von ?? ''}</div>}</>
                            : n.alt_datum ? <><span className="rot">abgesagt</span>, bisher {datum(n.alt_datum)} {n.alt_von ?? ''}</>
                            : n.wiedervorlage ? <>Wiedervorlage {datum(n.wiedervorlage)}</> : '–'}
                          {n.firma && <div className="leise">{n.firma}</div>}
                          {(q.art === 'gfap_buchen' || q.art === 'gfap_aendern') && (
                            n.ressource_name ? <div className="leise">Monteur: {n.ressource_name}</div>
                              : n.firma ? <div className="rot">kein Monteur bei {n.firma}</div> : null)}
                        </td>
                        <td>
                          <span className={`marke-zustand ${zs.farbe}`}>{zs.wort}</span>
                          {(q.fehler || q.ergebnis?.text) && <div className="klein leise" style={{ maxWidth: 360 }}>{q.fehler ?? q.ergebnis?.text}</div>}
                        </td>
                        <td className="knopfleiste">
                          {q.bild_datei_id && <button className="knopf klein" onClick={() => setBild(q.bild_datei_id)}>Bild</button>}
                          {darfEintragen && ['fehler', 'hand', 'probelauf'].includes(q.zustand) && <button className="knopf klein" onClick={() => tun(() => api.gbgsNochmal(q.id))}>Noch einmal</button>}
                          {darfEintragen && q.zustand !== 'erledigt' && q.zustand !== 'laeuft' && <button className="knopf klein" onClick={() => tun(() => api.gbgsErledigt(q.id))}>Erledigt</button>}
                          {darfEintragen && ['wartet', 'hand', 'fehler', 'probelauf'].includes(q.zustand) && <button className="knopf klein" onClick={async () => { if (await fragen('Diese Zeile verwerfen? Im Portal passiert dann nichts.')) void tun(() => api.gbgsVerwerfen(q.id)); }}>Verwerfen</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          
  );
}

export function SammelWartegrundFenster({ standorte, ich, zu, fertig }: { standorte: any[]; ich: Anmelder; zu: () => void; fertig: (text: string) => void }) {
  const [katalog, setKatalog] = useState<any>(null);
  useEffect(() => { if (katalog) setWv(wvZielIso()); }, [katalog]);
  const [vorlagen, setVorlagen] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [hashtag, setHashtag] = useState('');
  const [bulkGrund, setBulkGrund] = useState('');
  const [wv, setWv] = useState('');
  const [ende, setEnde] = useState('');
  const [freitext, setFreitext] = useState('');
  const [vorlageId, setVorlageId] = useState('');
  const [vorlageBulkId, setVorlageBulkId] = useState('');
  /* Der Termin wird immer zuerst frei (.460), ohne Haken. */
  const freigeben = true;
  useEffect(() => { api.gbgsKatalog().then(setKatalog).catch((e) => setFehler(ausFehler(e))); }, []);
  useEffect(() => { api.gbgsVorlagen().then((v) => setVorlagen(v.filter((x: any) => x.aktiv))).catch(() => setVorlagen([])); }, []);

  const gfap = (s: any) => (s.bauaufgaben ?? []).find((a: any) => istOffen(a) && ['GFAP_INSTALLATION', 'GFTA_INSTALLATION_GFAP'].includes(a.typ) && a.status !== 'HELD' && !a.grund);
  const istBulk = (s: any) => !gfap(s) && !!s.bulk;
  const ibtZeilen = standorte.filter((s) => gfap(s) && (gfap(s).produktionsmodus !== 'BFO'));
  const bfoZeilen = standorte.filter((s) => gfap(s) && gfap(s).produktionsmodus === 'BFO');
  const bulkZeilen = standorte.filter(istBulk);
  const gruende: any[] = (katalog?.wartegruende ?? []).filter((w: any) => w.modus === 'STANDARD');
  const grundText = gruende.find((w) => w.hashtag === hashtag)?.grund_text ?? '';
  const vorlagenIbt = vorlagen.filter((v) => v.hashtag ? String(v.hashtag).startsWith('0') : !v.bulk); const vorlagenBulk = vorlagen.filter((v) => v.hashtag ? String(v.hashtag).startsWith('5') : !!v.bulk);
  const vorlage = vorlagenIbt.find((v) => String(v.id) === vorlageId) ?? (hashtag ? vorlagenIbt.find((v) => v.hashtag === hashtag) : null) ?? vorlagenIbt.find((v) => !v.hashtag) ?? vorlagenIbt[0] ?? null;
  const vorlageBulk = vorlagenBulk.find((v) => String(v.id) === vorlageBulkId)
    ?? (bulkGrund ? vorlagenBulk.find((v) => v.hashtag && (katalog?.wartegruende ?? []).find((w: any) => w.hashtag === v.hashtag)?.kategorie_text === bulkGrund) : null)
    ?? vorlagenBulk.find((v) => !v.hashtag) ?? vorlagenBulk[0] ?? null;
  const jetzt = new Date();
  const werteFuer = (s: any, bulk: boolean): Record<string, string> => ({
    datum: new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' }).format(jetzt),
    uhrzeit: new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' }).format(jetzt),
    bearbeiter: ich.name ?? '', firma: '',
    grund: bulk ? bulkGrund : grundText, hashtag: !bulk && hashtag ? `#${hashtag}#` : '', wiedervorlage: deDatum(wv),
    adresse: anschrift(s.ort ?? s.bulk ?? s.bauauftraege?.[0] ?? null), kls: String(s.kls_id ?? ''), freitext: freitext.trim(),
  });
  const kommentarFuer = (s: any, bulk: boolean) => {
    const v = bulk ? vorlageBulk : vorlage;
    return v ? vorlageFuellen(v.text, werteFuer(s, bulk)) : freitext.trim();
  };
  const beispiel = standorte[0] ? kommentarFuer(standorte[0], istBulk(standorte[0])) : '';
  const bereit = !!wv && (!ibtZeilen.length || !!hashtag) && (!bulkZeilen.length || (!!bulkGrund && vorlagenBulk.length > 0 || !!freitext.trim()));

  const senden = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const eintraege = [
        ...ibtZeilen.map((s) => ({ kls: s.kls_id, bauaufgabe_bid: gfap(s).bid, hashtag, kommentar: kommentarFuer(s, false) })),
        ...bulkZeilen.map((s) => ({ kls: s.kls_id, gebaeudeauftrag_id: s.bulk.gebaeudeauftrag_id, grund_text: bulkGrund, kommentar: kommentarFuer(s, true) })),
      ];
      const r = await api.gbgsWartegrundSammel({ eintraege, wiedervorlage: wv, ende: ende || null, termin_freigeben: freigeben, telegram: freigeben ? await pushFrage(null, 'Die Firmen') : false });
      fertig(r.klartext);
    } catch (e: any) { setFehler(ausFehler(e)); } finally { setLaeuft(false); }
  };

  return (
    <Dialog titel="Wartegrund für die Auswahl" untertitel={`${standorte.length} Standorte: ${ibtZeilen.length} IBT, ${bulkZeilen.length} Bulk${bfoZeilen.length ? `, ${bfoZeilen.length} Bulk-Standorte an der Bauaufgabe (hier nicht, bitte einzeln)` : ''}`} breite={680} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {ibtZeilen.length > 0 && (
        <>
          <div className="feld"><label>Wartegrund für die {ibtZeilen.length} IBT-Standorte (Hashtags 0xxx)</label>
            <select value={hashtag} onChange={(e) => setHashtag(e.target.value)}>
              <option value="">bitte wählen</option>
              <GrundOptionen gruende={gruende} modus="STANDARD" />
            </select>
          </div>
          <div className="feld"><label>Textvorlage (IBT)</label>
            <select value={vorlageId} onChange={(e) => { setVorlageId(e.target.value); const v = vorlagenIbt.find((x) => String(x.id) === e.target.value); if (v?.hashtag && gruende.some((w) => w.hashtag === v.hashtag)) setHashtag(v.hashtag); }}>
              {!vorlagenIbt.length && <option value="">keine Vorlage</option>}
              {vorlagenIbt.map((v) => <option key={v.id} value={v.id}>{v.titel}{v.hashtag ? ` (#${v.hashtag}#)` : ''}</option>)}
            </select>
          </div>
        </>
      )}
      {bulkZeilen.length > 0 && (
        <>
          <div className="feld"><label>Wartegrund für die {bulkZeilen.length} Bulk-Standorte</label>
            <select value={bulkGrund} onChange={(e) => setBulkGrund(e.target.value)}>
              <option value="">bitte wählen</option>
              <BulkOptionen gruende={katalog?.bulk ?? []} />
            </select>
          </div>
          <div className="feld"><label>Textvorlage (Bulk)</label>
            <select value={vorlageBulkId} onChange={(e) => { setVorlageBulkId(e.target.value); const v = vorlagenBulk.find((x) => String(x.id) === e.target.value); const k = v?.hashtag ? (katalog?.wartegruende ?? []).find((w: any) => w.hashtag === v.hashtag)?.kategorie_text : null; if (k && (katalog?.bulk ?? []).includes(k)) setBulkGrund(k); }}>
              {!vorlagenBulk.length && <option value="">keine Vorlage</option>}
              {vorlagenBulk.map((v) => <option key={v.id} value={v.id}>{v.titel}</option>)}
            </select>
          </div>
        </>
      )}
      <div className="reihe" style={{ gap: 12 }}>
        <div style={{ flex: 1 }}><WvHinweis fristen={katalog?.fristen} fristenBulk={katalog?.fristen_bulk} hashtag={hashtag} bulkGrund={bulkGrund} /></div>
        <div className="feld" style={{ flex: 1 }}><label>Voraussichtliches Ende (optional)</label><input type="date" value={ende} onChange={(e) => setEnde(e.target.value)} /></div>
      </div>
      <div className="feld"><label>Eigener Text <span className="rot">(Achtung: für die Telekom sichtbar, bitte in sauberen, vollständigen Sätzen)</span></label><textarea rows={3} value={freitext} onChange={(e) => setFreitext(e.target.value)} placeholder="z. B. Der Kunde war telefonisch mehrfach nicht erreichbar, wir haben um Rückruf gebeten." /></div>
      <FreitextBausteine wert={freitext} setze={(t) => setFreitext(/[.!?]$/.test(t) ? t : t + '.')}
        vorschlaege={[...new Set([...freitextVorschlaege(gruende.find((w) => w.hashtag === hashtag)?.grund), ...freitextVorschlaege(bulkGrund)])]} />
      {beispiel && <div className="hinweis" style={{ whiteSpace: 'pre-wrap' }}><div className="klein leise">So geht der Kommentar am ersten Standort ins Portal:</div>{beispiel}</div>}
      <p className="klein leise">Hängt an einem Standort noch ein Termin, wird er zuerst storniert (IBT: Absage am Grundstück), danach kommt der Wartegrund.</p>
      <div className="reihe" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        <button type="button" className="knopf haupt" disabled={laeuft || !bereit} onClick={senden}>{standorte.length} Wartegründe in die Warteschlange</button>
      </div>
    </Dialog>
  );
}

/* DER AUFBAU DER NACHRICHT (.348).
   ORIGINALTEXT MARVIN (11.10.2026)
     „und wo ist die auswahl der aufbau der nachricht, wie sie geschrieben
      sein muss. das müssen wir noch hinterlegen."
   Eine Vorlage ist ein Text mit Platzhaltern in geschweiften Klammern. Im
   Fenster wird daraus der Kommentar, den man vor dem Absenden noch ändern
   kann. Hat eine Vorlage einen Hashtag, wird sie bei diesem Grund vorgewählt. */
const PLATZHALTER: [string, string][] = [
  ['datum', 'heutiges Datum'], ['uhrzeit', 'Uhrzeit jetzt'], ['bearbeiter', 'wer den Grund setzt'], ['firma', 'Firma am Termin'],
  ['grund', 'Text des Wartegrunds'], ['hashtag', 'Hashtag mit Rauten'], ['wiedervorlage', 'Datum der Wiedervorlage'],
  ['adresse', 'Anschrift'], ['kls', 'KLS-ID'], ['freitext', 'was im Fenster dazu geschrieben wird'],
];
const deDatum = (iso: string) => iso ? iso.slice(0, 10).split('-').reverse().join('.') : '';
export function vorlageFuellen(text: string, w: Record<string, string>) {
  return text.replace(/\{(\w+)\}/g, (_, k) => w[k] ?? '').replace(/[ \t]{2,}/g, ' ').replace(/\s+([.,])/g, '$1').replace(/\.\./g, '.').trim().replace(/[\s:]+$/, '');
}

function Textvorlagen() {
  const [liste, setListe] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [bearb, setBearb] = useState<any | null>(null);
  const laden = async () => { try { setListe(await api.gbgsVorlagen()); } catch (e) { setFehler(ausFehler(e)); } };
  useFrisch(laden);
  useEffect(() => { void laden(); }, []);
  const speichern = async () => {
    setFehler(null);
    try {
      const b = { titel: bearb.titel, hashtag: bearb.hashtag || null, bulk: !!bearb.bulk, text: bearb.text, reihenfolge: Number(bearb.reihenfolge ?? 100), aktiv: bearb.aktiv !== false };
      if (bearb.id) await api.gbgsVorlageAendern(bearb.id, b); else await api.gbgsVorlageAnlegen(b);
      setBearb(null); await laden();
    } catch (e) { setFehler(ausFehler(e)); }
  };
  return (
    <Karte id="gbgs-textvorlagen" titel="Textvorlagen für den Wartegrund"
           rechts={<button className="knopf klein" onClick={() => setBearb({ titel: '', text: '{datum} MDS Innovation Ltd. MSC: {hashtag}: {freitext}', bulk: false, reihenfolge: 100, aktiv: true })}>Neue Vorlage</button>}>
      <Fehlerkasten lage={bearb ? null : fehler} />
      {!liste ? <p className="klein leise">Wird geladen.</p> : !liste.length ? <Leer text="Noch keine Vorlage." /> : (
        <div className="tabellenrahmen">
          <table className="liste">
            <thead><tr><th>Name</th><th>Für</th><th>Text</th><th></th></tr></thead>
            <tbody>
              {liste.map((v) => (
                <tr key={v.id} style={{ cursor: 'default' }}>
                  <td><b>{v.titel}</b>{!v.aktiv && <span className="klein leise"> (aus)</span>}</td>
                  <td className="klein">{v.bulk ? 'Bulk' : 'Bauaufgabe'}{v.hashtag ? `, #${v.hashtag}#` : ''}</td>
                  <td className="klein" style={{ maxWidth: 480 }}>{v.text}</td>
                  <td className="knopfleiste">
                    <button className="knopf klein" onClick={() => setBearb({ ...v })}>Ändern</button>
                    <button className="knopf klein" onClick={async () => { if (await fragen(`Vorlage „${v.titel}" löschen?`)) { try { await api.gbgsVorlageLoeschen(v.id); await laden(); } catch (e) { setFehler(ausFehler(e)); } } }}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="klein leise">Platzhalter: {PLATZHALTER.map(([k, t]) => <span key={k}><code>{`{${k}}`}</code> {t}; </span>)}</p>
      {bearb && (
        <Dialog titel={bearb.id ? 'Vorlage ändern' : 'Neue Vorlage'} breite={620} zu={() => setBearb(null)}>
          <Fehlerkasten lage={fehler} />
          <div className="feld"><label>Name</label><input value={bearb.titel} onChange={(e) => setBearb({ ...bearb, titel: e.target.value })} /></div>
          <div className="reihe" style={{ gap: 12 }}>
            <div className="feld"><label>Für</label>
              <select value={bearb.bulk ? 'ja' : 'nein'} onChange={(e) => setBearb({ ...bearb, bulk: e.target.value === 'ja' })}>
                <option value="nein">Bauaufgabe (CT)</option><option value="ja">Bulk-Auftrag</option>
              </select>
            </div>
            <div className="feld"><label>Nur bei Hashtag (leer = alle)</label><input value={bearb.hashtag ?? ''} placeholder="z. B. 0123" onChange={(e) => setBearb({ ...bearb, hashtag: e.target.value.replace(/#/g, '') })} /></div>
            <div className="feld"><label>Reihenfolge</label><input type="number" value={bearb.reihenfolge ?? 100} onChange={(e) => setBearb({ ...bearb, reihenfolge: e.target.value })} /></div>
          </div>
          <div className="feld"><label>Text</label><textarea rows={4} value={bearb.text} onChange={(e) => setBearb({ ...bearb, text: e.target.value })} /></div>
          <p className="klein leise">Beispiel: {vorlageFuellen(bearb.text ?? '', { datum: '14.10.2026', uhrzeit: '09:15', bearbeiter: 'Max', firma: 'Muster GmbH', grund: 'Kunde nicht erreichbar', hashtag: '#0000#', wiedervorlage: '21.10.2026', adresse: 'Musterweg 1, 12345 Musterstadt', kls: '000000000', freitext: '' })}</p>
          <label className="klein"><input type="checkbox" checked={bearb.aktiv !== false} onChange={(e) => setBearb({ ...bearb, aktiv: e.target.checked })} /> in der Auswahl zeigen</label>
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf haupt" disabled={!String(bearb.titel ?? '').trim() || !String(bearb.text ?? '').trim()} onClick={speichern}>Speichern</button>
            <button className="knopf" onClick={() => setBearb(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
    </Karte>
  );
}

export function WartegrundFenster({ standort, ich, zu, fertig }: { standort: any; ich: Anmelder; zu: () => void; fertig: (text: string) => void }) {
  const [katalog, setKatalog] = useState<any>(null);
  useEffect(() => { if (katalog) setWv(wvZielIso()); }, [katalog]);
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [laeuft, setLaeuft] = useState(false);
  /* NUR DER GF-AP (.460). Marvin, 22.10.2026: „ausschließlich bei GFAP, weil
     das ist der Punkt, den die Telekom ausliest". Hausstich, TA, Vzk und
     Spleiß stehen hier nicht mehr zur Auswahl. */
  const offen: any[] = (standort.bauaufgaben ?? []).filter(istOffen).filter((a: any) => a.typ === 'GFAP_INSTALLATION');
  const bulkMoeglich = !!standort.bulk;
  /* BULK-STANDORT: NUR AM BULK-AUFTRAG (.555). Wallersheimer Weg 66, 30.10.2026:
     das Fenster stand auf „an der Bauaufgabe", und das Portal lehnte ab
     („Appointment already exists"), weil der Gf-AP-Termin im Bulk-Portal steht
     und sich dort nicht aufheben lässt. Bei Bulk gehört der Wartegrund an den
     Bulk-Auftrag (Marvin, 14.10.2026). */
  const nurBulk = bulkMoeglich && offen.every((a: any) => a.produktionsmodus === 'BFO');
  const [bulk, setBulk] = useState<boolean>(nurBulk || (!offen.length && bulkMoeglich));
  const [aufgabe, setAufgabe] = useState<string>(String(offen.find((a) => a.typ === 'GFAP_INSTALLATION')?.bid ?? offen[0]?.bid ?? ''));
  const [hashtag, setHashtag] = useState('');
  const [grundText, setGrundText] = useState('');
  /* Wiedervorlage vorbelegt: in 14 Tagen (.460), änderbar. */
  const [wv, setWv] = useState(() => new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10));
  const [ende, setEnde] = useState('');
  const [freitext, setFreitext] = useState('');
  const [vorlagen, setVorlagen] = useState<any[]>([]);
  const [vorlageId, setVorlageId] = useState<string>('');
  const [kommentar, setKommentar] = useState('');
  const [vonHand, setVonHand] = useState(false);
  const [mussFreigeben, setMussFreigeben] = useState<string | null>(null);
  useEffect(() => { api.gbgsKatalog().then(setKatalog).catch((e) => setFehler(ausFehler(e))); }, []);
  useEffect(() => { api.gbgsVorlagen().then((v) => setVorlagen(v.filter((x: any) => x.aktiv))).catch(() => setVorlagen([])); }, []);

  const gewaehlt = offen.find((a) => String(a.bid) === aufgabe);
  const modus = gewaehlt?.produktionsmodus === 'BFO' ? 'BFO' : 'STANDARD';
  const gruende: any[] = (katalog?.wartegruende ?? []).filter((w: any) => w.modus === modus);
  const grundGewaehlt = bulk ? grundText : (gruende.find((w) => w.hashtag === hashtag)?.grund_text ?? '');

  /* Kundentermin am Gf-AP: Einblasen/Spleißen der Plattform und der gebuchte
     Gf-AP im Portal müssen zuerst frei werden (.348). */
  // Nur IBT (Marvin: „das gilt nur für die ibt aufträge."): nicht am Bulk-Auftrag, nicht an Bulk-Standorten (BFO).
  const amGfap = !bulk && modus !== 'BFO' && ['GFAP_INSTALLATION', 'GFTA_INSTALLATION_GFAP'].includes(String(gewaehlt?.typ ?? ''));
  const plattformTermine: any[] = standort.auftrag?.termine ?? [];
  /* Nur der Gf-AP (.457): die TA (termin_status am Bauauftrag) bleibt unberührt. */
  const portalGebucht = !bulk && ((standort.gebaeudeteile ?? []).some((t: any) => t.termin_status === 'TIMESLOT_BOOKED')
    || (standort.bauauftraege ?? []).some((o: any) => o.gfap_termin_status === 'TIMESLOT_BOOKED'));
  const terminHaengt = amGfap && (plattformTermine.length > 0 || portalGebucht || !!mussFreigeben);

  /* NUR DIE VORLAGEN DES BEREICHS (.460): IBT 0xxx, Bulk-Standort und
     Bulk-Auftrag 5xxx. Eine Vorlage mit Hashtag steht nur da, wenn ihr Hashtag
     zur Liste gehört; sonst kann niemand auseinanderhalten, was wohin gehört. */
  const bulkBereich = bulk || modus === 'BFO';
  const passende = vorlagen.filter((v) => v.hashtag
    ? (bulkBereich ? String(v.hashtag).startsWith('5') : String(v.hashtag).startsWith('0'))
    : !!v.bulk === bulkBereich);
  /* WER EINE VORLAGE MIT HASHTAG WÄHLT, WÄHLT DAMIT DEN GRUND (.460).
     Marvin: „wenn ich ... eine Textvorlage, Verhinderung durch Dritte, dann
     sollte er doch oben auch den Bereich Wartegrund auswählen." */
  const vorlageWaehlen = (id: string) => {
    setVorlageId(id); setVonHand(false);
    const v = passende.find((x) => String(x.id) === id);
    if (!v?.hashtag) return;
    if (bulk) {
      const k = (katalog?.wartegruende ?? []).find((w: any) => w.hashtag === v.hashtag)?.kategorie_text;
      if (k && (katalog?.bulk ?? []).includes(k)) setGrundText(k);
    } else if (gruende.some((w) => w.hashtag === v.hashtag)) setHashtag(v.hashtag);
  };
  const vorlage = passende.find((v) => String(v.id) === vorlageId)
    ?? (!bulk && hashtag ? passende.find((v) => v.hashtag === hashtag) : null)
    /* BULK (.458): die Vorlage zum gewählten Grund, über die Kategorie ihres
       Hashtags (5102/5104/5105 Bauverhinderung, 5401 Eigentümer nicht erreichbar). */
    ?? (bulk && grundText ? passende.find((v) => v.hashtag && (katalog?.wartegruende ?? []).find((w: any) => w.hashtag === v.hashtag)?.kategorie_text === grundText) : null)
    ?? passende.find((v) => !v.hashtag) ?? passende[0] ?? null;
  const jetzt = new Date();
  const werte: Record<string, string> = {
    datum: new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' }).format(jetzt),
    uhrzeit: new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' }).format(jetzt),
    bearbeiter: ich.name ?? '', firma: plattformTermine.find((t) => t.firma)?.firma ?? '',
    grund: grundGewaehlt, hashtag: !bulk && hashtag ? `#${hashtag}#` : '', wiedervorlage: deDatum(wv),
    adresse: anschrift(standort.ort ?? standort.bulk ?? standort.bauauftraege?.[0] ?? null), kls: String(standort.kls_id ?? ''), freitext: freitext.trim(),
  };
  const vorschlag = vorlage ? vorlageFuellen(vorlage.text, werte) : freitext.trim();
  useEffect(() => { if (!vonHand) setKommentar(vorschlag); }, [vorschlag, vonHand]);

  const senden = async () => {
    setLaeuft(true); setFehler(null);
    try {
      /* ERST FRAGEN, DANN PUSHEN (.358): storniert der Wartegrund einen Termin der Plattform, entscheidet der Mensch über die Absage an die Firma. */
      /* DER TERMIN WIRD IMMER ZUERST FREI (.460). Marvin: „Wenn wir einen
         Wartegrund setzen, dann ist es selbstverständlich, dass der Termin
         storniert wird. Ansonsten kannst du keinen Wartegrund setzen." */
      const extra: Record<string, unknown> = { termin_freigeben: true };
      const mitAbsage = plattformTermine.length > 0;
      Object.assign(extra, { telegram: mitAbsage ? await pushFrage(plattformTermine[0]?.termin_datum ?? plattformTermine[0]?.datum ?? null) : false });
      const r = bulk
        ? await api.gbgsWartegrund({ gebaeudeauftrag_id: standort.bulk.gebaeudeauftrag_id, grund_text: grundText, wiedervorlage: wv, ende: ende || null, kommentar, ...extra })
        : await api.gbgsWartegrund({ bauaufgabe_bid: Number(aufgabe), hashtag, wiedervorlage: wv, ende: ende || null, kommentar, ...extra });
      fertig(r.klartext);
    } catch (e: any) {
      if (e?.daten?.feld === 'termin_freigeben') { setMussFreigeben(e.daten.hinweis ?? ''); }
      else setFehler(ausFehler(e));
    } finally { setLaeuft(false); }
  };

  return (
    <Dialog titel="Wartegrund im GBGS" untertitel={`KLS ${standort.kls_id}`} breite={620} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {nurBulk && offen.length > 0 && (
        <div className="hinweis klein" style={{ marginBottom: 8 }}>Bulk-Standort: der Wartegrund kommt an den Bulk-Auftrag. Die Gf-AP-Bauaufgabe nimmt keinen, solange der Gf-AP-Termin im Bulk-Portal steht.</div>
      )}
      {bulkMoeglich && offen.length > 0 && !nurBulk && (
        <div className="reihe" style={{ gap: 12, marginBottom: 8 }}>
          <label className="klein"><input type="radio" checked={!bulk} onChange={() => setBulk(false)} /> an der Bauaufgabe</label>
          <label className="klein"><input type="radio" checked={bulk} onChange={() => setBulk(true)} /> am Bulk-Auftrag</label>
        </div>
      )}
      {!bulk && !offen.length && (
        <div className="hinweis fehler">Am Standort gibt es keine offene Bauaufgabe Gf-AP. Der Wartegrund gehört an den Gf-AP, nicht an Hausstich oder TA.</div>
      )}
      {!bulk ? (
        <>
          <div className="feld"><label>Bauaufgabe</label>
            <select value={aufgabe} onChange={(e) => { setAufgabe(e.target.value); setHashtag(''); }}>
              {offen.map((a) => <option key={a.bid} value={a.bid}>{TYP[a.typ] ?? a.typ} ({STATUS[a.status]?.wort ?? a.status}){a.grund ? `, hat schon: ${a.grund}` : ''}</option>)}
            </select>
          </div>
          <div className="feld"><label>Wartegrund ({modus === 'BFO' ? 'Bulk-Standort, Hashtags 5xxx' : 'Hashtags 0xxx'})</label>
            <select value={hashtag} onChange={(e) => setHashtag(e.target.value)}>
              <option value="">bitte wählen</option>
              <GrundOptionen gruende={gruende} modus={modus} />
            </select>
          </div>
        </>
      ) : (
        <div className="feld"><label>Grund (Bulk-Portal)</label>
          <select value={grundText} onChange={(e) => setGrundText(e.target.value)}>
            <option value="">bitte wählen</option>
            <BulkOptionen gruende={katalog?.bulk ?? []} />
          </select>
        </div>
      )}
      <div className="reihe" style={{ gap: 12 }}>
        <div style={{ flex: 1 }}><WvHinweis fristen={katalog?.fristen} fristenBulk={katalog?.fristen_bulk} hashtag={bulk ? null : hashtag} bulkGrund={bulk ? grundText : null} /></div>
        <div className="feld"><label>Erwartetes Ende (frei)</label><input type="date" value={ende} onChange={(e) => setEnde(e.target.value)} /></div>
      </div>
      <div className="feld"><label>Textvorlage</label>
        <select value={vorlage ? String(vorlage.id) : ''} onChange={(e) => vorlageWaehlen(e.target.value)}>
          {!passende.length && <option value="">keine Vorlage</option>}
          {passende.map((v) => <option key={v.id} value={v.id}>{v.titel}{v.hashtag ? ` (#${v.hashtag}#)` : ''}</option>)}
        </select>
      </div>
      <div className="feld"><label>Eigener Text <span className="rot">(Achtung: für die Telekom sichtbar, bitte in sauberen, vollständigen Sätzen)</span></label>
        <textarea rows={3} value={freitext} onChange={(e) => { setFreitext(e.target.value); setVonHand(false); }}
                  placeholder="z. B. Der Kunde war telefonisch mehrfach nicht erreichbar, wir haben um Rückruf gebeten." />
      </div>
      <FreitextBausteine wert={freitext} setze={(t) => { setFreitext(/[.!?]$/.test(t) ? t : t + '.'); setVonHand(false); }}
        vorschlaege={freitextVorschlaege(bulk ? grundText : gruende.find((w) => w.hashtag === hashtag)?.grund)} />
      {/* DIE VORSCHAU IST KEIN EINGABEFELD (.460). Marvin: „die Vorschau sieht
          nämlich aus wie ein Texteingabefeld". Sie steht als Kasten da; wer den
          Satz von Hand ändern will, schaltet ausdrücklich um. */}
      <div className="feld"><label>So geht der Kommentar ins Portal{bulk ? ' (Pflicht im Bulk-Portal)' : ''}</label>
        {vonHand ? (
          <>
            <textarea rows={3} value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
            <button type="button" className="alslink klein" onClick={() => setVonHand(false)}>wieder aus Vorlage und eigenem Text bauen</button>
          </>
        ) : (
          <div className="hinweis" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {kommentar || <span className="leise">Erst Wartegrund und eigenen Text wählen.</span>}
            <div><button type="button" className="alslink klein" onClick={() => setVonHand(true)}>von Hand ändern</button></div>
          </div>
        )}
      </div>
      {terminHaengt && (
        <div className="hinweis" style={{ marginBottom: 8 }}>
          <b>So läuft es ab:</b> Der Termin wird zuerst storniert, danach setzt der Roboter den Wartegrund am Gf-AP.
          <div className="klein" style={{ whiteSpace: 'pre-line', fontWeight: 400, marginTop: 4 }}>
            {mussFreigeben ?? [
              ...plattformTermine.map((t) => `${t.art === 'EB' ? 'Einblasen' : 'Spleißen'} am ${datum(t.datum)}${t.von ? ' ' + String(t.von).slice(0, 5) : ''}${t.firma ? ' (' + t.firma + ')' : ''} wird in der Plattform storniert.`),
              ...(portalGebucht ? ['Der Gf-AP-Termin im GBGS wird am Grundstück abgesagt (Absage des Termins).'] : []),
            ].join('\n')}
          </div>
        </div>
      )}
      <p className="klein leise">Die Zeile geht in die Warteschlange. Mit Roboter trägt er sie im Portal ein und meldet mit Bild; ohne steht sie auf der Handliste.</p>
      <div className="reihe">
        <button type="button" className="knopf haupt" disabled={laeuft || !wv || (bulk ? !grundText || !kommentar.trim() : !hashtag || !aufgabe)} onClick={senden}>Wartegrund setzen</button>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
